import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Paper,
  Divider
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  SupervisorAccount as StaffIcon,
  Payment as PaymentIcon,
  Assessment as ReportsIcon,
  Security as SecurityIcon,
  Church as ChurchIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, canAccessCCD, canAccessParish, canAccessStaff, canAccessMass, canAccessCampusMinistry } = useAuth();

  const navigationCards = [];

  // Add cards based on permissions
  if (canAccessCCD()) {
    navigationCards.push(
      {
        title: 'CCD Registrations',
        description: 'Manage Religious Education registrations',
        icon: <SchoolIcon sx={{ fontSize: 48, color: '#1976d2' }} />,
        path: '/religious-education',
        color: '#e3f2fd'
      },
      {
        title: 'CCD Settings',
        description: 'Configure Religious Education settings',
        icon: <SettingsIcon sx={{ fontSize: 48, color: '#f57c00' }} />,
        path: '/religious-education/settings',
        color: '#fff3e0'
      }
    );
  }

  if (canAccessParish()) {
    navigationCards.push(
      {
        title: 'Parish Registrations',
        description: 'Manage parish family registrations',
        icon: <PeopleIcon sx={{ fontSize: 48, color: '#388e3c' }} />,
        path: '/parish-registration',
        color: '#e8f5e8'
      },
      {
        title: 'Parish Settings',
        description: 'Configure parish registration settings',
        icon: <SettingsIcon sx={{ fontSize: 48, color: '#7b1fa2' }} />,
        path: '/parish-registration/settings',
        color: '#f3e5f5'
      }
    );
  }

  if (canAccessStaff()) {
    navigationCards.push({
      title: 'Staff Management',
      description: 'Manage staff accounts and permissions',
      icon: <StaffIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
      path: '/staff',
      color: '#ffebee'
    });
  }

  if (canAccessMass()) {
    navigationCards.push({
      title: 'Mass Management',
      description: 'Manage Mass schedules, intentions, and clergy assignments',
      icon: <ChurchIcon sx={{ fontSize: 48, color: '#8e24aa' }} />,
      path: '/mass',
      color: '#f3e5f5'
    });
  }

  if (canAccessCampusMinistry()) {
    navigationCards.push({
      title: 'Campus Ministry',
      description: 'Manage Fall Retreat and other Campus Ministry signups',
      icon: <GroupsIcon sx={{ fontSize: 48, color: '#00897b' }} />,
      path: '/campus-ministry',
      color: '#e0f2f1'
    });
  }

  const quickStatsCards = [
    {
      title: 'Payment Lookup',
      description: 'Families can lookup and pay remaining balances',
      icon: <PaymentIcon sx={{ fontSize: 36, color: '#2e7d32' }} />,
      info: 'Available for CCD registrations',
      color: '#e8f5e8'
    },
    {
      title: 'Registration Status',
      description: 'Track registration periods and submissions',
      icon: <ReportsIcon sx={{ fontSize: 36, color: '#1565c0' }} />,
      info: 'Real-time monitoring',
      color: '#e3f2fd'
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Welcome Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome to St. Bridget Staff Portal
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {user ? `Hello, ${user.name}!` : 'Welcome!'} Manage registrations, settings, and more from your centralized dashboard.
        </Typography>
      </Paper>

      {/* Main Navigation Cards */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
        System Management
      </Typography>

      {navigationCards.length > 0 ? (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {navigationCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(card.path)}
                  sx={{ height: '100%', p: 2 }}
                >
                  <CardContent sx={{ textAlign: 'center', height: '100%' }}>
                    <Box
                      sx={{
                        backgroundColor: card.color,
                        borderRadius: '50%',
                        width: 80,
                        height: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px auto'
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 5 }}>
          <SecurityIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Access Permissions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't currently have permission to access any systems. Please contact your administrator to request access.
          </Typography>
        </Paper>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Quick Info Section */}
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
        System Features
      </Typography>

      <Grid container spacing={3}>
        {quickStatsCards.map((card, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: card.color,
                      borderRadius: 2,
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.info}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body1">
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Additional Info */}
      <Box sx={{ mt: 4, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          System Information
        </Typography>
        <Typography variant="body2" paragraph>
          This staff portal provides centralized access to manage both Religious Education (CCD) and Parish registrations.
          Each system maintains its own settings, registration periods, and payment processing.
        </Typography>
        <Typography variant="body2">
          <strong>New Feature:</strong> Families can now lookup their CCD registrations and pay remaining balances online
          through the payment lookup system integrated with Square payments.
        </Typography>
      </Box>
    </Container>
  );
};

export default Dashboard;